<template>
  <div class="slider-vehicle" v-if="isLoading || vehicles?.length > 0">
    <!-- Título Desktop -->
    <h2 v-if="!isMobileView" class="carousel-title d-flex align-center">
      <span>
        <v-img
          v-if="icon"
          :width="35"
          aspect-ratio="1/1"
          :src="getIconSrc(icon)"
        />
      </span>
      <span class="ml-2">{{ title }}</span>

      <a
        class="ml-auto"
        style="font-size: 14px; color: #1976d2; text-decoration: underline; cursor: pointer;"
        @click="navigateToVehicleList(key_showcase)"
      >
        Acessar todos os anúncios
      </a>
    </h2>

    <h1 v-else class="d-flex align-center" style="font-size: 18px;" @click="navigateToVehicleList(key_showcase)">
      <img v-if="icon && getIconSrc(icon)" :src="getIconSrc(icon)" class="mr-2" style="width: 25px;" />
      {{ title }}
    </h1>

    <p class="subtitle">{{ subtitle }}</p>

    <!-- Skeleton -->
    <div v-if="isLoading" class="mx-4">
      <v-row v-if="!isMobileView">
        <v-col v-for="n in cardsPerSlide" :key="n">
          <v-skeleton-loader class="mx-auto border card-skeleton" type="image, article, actions" />
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col v-for="n in 3" :key="n" cols="12">
          <v-skeleton-loader class="mx-auto border card-skeleton" type="article, actions" />
        </v-col>
      </v-row>
    </div>

    <!-- Carrossel Desktop (bom) -->
    <v-carousel
      v-if="vehicles?.length > 0 && !isMobileView && !isLoading"
      height="540px"
      show-arrows
      show-delimiters
      hide-delimiter-background
      :cycle="true"
      :interval="4000"
      class="desktop-carousel"
      transition="none"
    >
      <!-- Setas -->
      <template v-slot:prev="{ props }">
        <v-btn class="carousel-arrow left" @click="props.onClick">
          <i class="mdi mdi-chevron-left"></i>
        </v-btn>
      </template>
      <template v-slot:next="{ props }">
        <v-btn class="carousel-arrow right" @click="props.onClick">
          <i class="mdi mdi-chevron-right"></i>
        </v-btn>
      </template>

      <v-carousel-item v-for="(chunk, chunkIndex) in chunkedVehicles" :key="chunkIndex">
        <div class="vehicle-row" :class="`cols-${cardsPerSlide}`">
          <div
            v-for="(vehicle, index) in chunk"
            :key="vehicle.id_avaliacao"
            class="vehicle-col"
          >
            <VehicleItem :vehicle="vehicle" :index="index" :linha="index" />
          </div>
        </div>
      </v-carousel-item>
    </v-carousel>

    <!-- Carrossel Mobile (bom) -->
    <v-sheet class="mobile" v-if="vehicles?.length > 0 && isMobileView && !isLoading" :key="vehicles.length" fluid>
      <v-carousel
        class="mobile-carousel"
        :height="mobileCarouselHeight"
        show-arrows
        show-delimiters
        hide-delimiter-background
        cycle
        transition="none"
      >
        <!-- Setas padrão do Vuetify no mobile (com posicionamento via CSS) -->
        <template v-slot:prev="{ props }">
          <v-btn class="carousel-arrow prev" icon @click="props.onClick">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
        </template>
        <template v-slot:next="{ props }">
          <v-btn class="carousel-arrow next" icon @click="props.onClick">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </template>

        <v-carousel-item
          v-for="(vehicle, index) in vehicles"
          :key="vehicle.id_avaliacao"
          :index="index"
        >
          <VehicleItem :vehicle="vehicle" :index="index" :linha="index" />
        </v-carousel-item>
      </v-carousel>
    </v-sheet>
  </div>
</template>

<script>
import VehicleItem from "@/components/VehicleItem/index.vue";
import { VSkeletonLoader } from "vuetify/components";
import { mapState } from "vuex";
import PlatformsMixins from "@/mixins/PlatformsMixins";
import PATHS from "@/constants/Paths";

export default {
  name: "VehiclesSlider",
  mixins: [PlatformsMixins],
  props: {
    vehicles: Array,
    isMobile: Boolean, // continua aceitando prop externa
    title: String,
    subtitle: String,
    icon: String,
    key_showcase: String,
  },
  components: {
    VSkeletonLoader,
    VehicleItem,
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      mobileCarouselHeight: 190,
    };
  },
  computed: {
    ...mapState({
      loadingMap: (state) => state.filterForm.loadingCampaign,
    }),
    isLoading() {
      return this.loadingMap[this.key_showcase] ?? true;
    },
    isMobileView() {
      return this.isMobile || this.windowWidth < 768;
    },
    cardsPerSlide() {
      if (this.isMobileView) return 1;
      if (this.windowWidth < 1400 && this.windowWidth >= 1000) return 3;
      if (this.windowWidth < 1000) return 2;
      return 4;
    },
    chunkedVehicles() {
      const chunks = [];
      for (let i = 0; i < this.vehicles.length; i += this.cardsPerSlide) {
        chunks.push(this.vehicles.slice(i, i + this.cardsPerSlide));
      }
      return chunks;
    },
  },
  watch: {
    vehicles: {
      immediate: true,
      handler(newVehicles) {
        if (newVehicles?.length > 0) {
          this.$nextTick(() => {
            this.preventLegacyCarouselConflict();
          });
        }
      }
    }
  },
  mounted() {
    this.preventLegacyCarouselConflict();
  },
  methods: {
    navigateToVehicleList(key) {
      const fullUrl = `${window.location.origin}` + PATHS.Showcase.vehicles.replace(':slug', key);
      window.open(fullUrl, '_self');
    },
    getIconSrc(name) {
      if (!name) return null;
      if (/^https?:\/\//i.test(name)) return name;
      try {
        return require(`@/assets/images/showcase/${name}`);
      } catch (e) {
        return null;
      }
    },
    preventLegacyCarouselConflict() {
      if (typeof window !== 'undefined' && window.$ && window.$.fn && window.$.fn.owlCarousel) {
        window.$.fn.owlCarousel = function() {
          console.warn('Owl Carousel disabled to prevent conflicts with Vue carousel');
          return this;
        };
      }

      this.$nextTick(() => {
        const owlElements = document.querySelectorAll('.owl-carousel, .owl-stage, .owl-stage-outer, .owl-item, .owl-nav, .owl-dots');
        owlElements.forEach(el => {
          el.classList.remove('owl-carousel', 'owl-stage', 'owl-stage-outer', 'owl-item', 'owl-nav', 'owl-dots');
          el.removeAttribute('style');
        });

        const carousels = document.querySelectorAll('.v-carousel');
        carousels.forEach(carousel => {
          carousel.style.display = 'none';
          setTimeout(() => {
            carousel.style.display = '';
          }, 10);
        });
      });

      if (typeof window !== 'undefined') {
        window._checkVisibile = function() { return true; };
        const originalQuerySelectorAll = document.querySelectorAll;
        document.querySelectorAll = function(selector) {
          if (selector && selector.includes('owl')) {
            return [];
          }
          return originalQuerySelectorAll.call(this, selector);
        };
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* ===== Desktop (bom) ===== */
.desktop-carousel {
  position: relative;
  margin: 2rem auto;
  height: 540px;
  overflow: visible;

  .v-carousel-item {
    height: 100%;
    display: flex;
    align-items: stretch;
    justify-content: center;
    overflow: visible;
  }

  .vehicle-row {
    --gap: 12px;
    width: 100%;
    display: flex;
    flex-wrap: nowrap;
    align-items: stretch;
    justify-content: center;
    gap: var(--gap);
  }

  .vehicle-row.cols-4 .vehicle-col {
    flex: 0 0 calc((100% - (3 * var(--gap))) / 4);
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .vehicle-row.cols-3 .vehicle-col {
    flex: 0 0 calc((100% - (2 * var(--gap))) / 3);
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .vehicle-row.cols-2 .vehicle-col {
    flex: 0 0 calc((100% - (1 * var(--gap))) / 2);
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .vehicle-col .vehicle-item {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  /* Setas desktop */
  .carousel-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 5;
    background: #fff;
    border-radius: 50%;
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);

    i { font-size: 24px; color: #333; }
    &:hover { background: #f5f5f5; }
  }
  .carousel-arrow.left  { left: -20px; }
  .carousel-arrow.right { right: -20px; }

  /* Dots desktop */
  .v-carousel__controls {
    position: absolute;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;

    .v-btn {
      width: 10px; height: 10px;
      border-radius: 50%;
      margin: 0 4px;
      background-color: #ccc;
      min-width: 10px; min-height: 10px;
      box-shadow: none;

      &.v-btn--active { background-color: #ff6600; }
    }
  }

  /* Ajustes de gap por largura */
  @media (min-width: 1780px) and (max-width: 1870px) { .vehicle-row { --gap: 16px; } }
  @media (max-width: 1700px) { .vehicle-row { --gap: 12px; } }
  @media (max-width: 1500px) { .vehicle-row { --gap: 10px; } }
  @media (max-width: 1400px) { .vehicle-row { --gap: 8px; } }
  @media (max-width: 1200px) {
    .vehicle-row { --gap: 8px; }
    .carousel-arrow.left  { left: 6px; }
    .carousel-arrow.right { right: 6px; }
  }
}

/* ===== Mobile (bom) — aplica até 1000px ===== */
@media (max-width: 1000px) {
  .mobile-carousel {
    position: relative;
    padding-bottom: 36px; /* reserva para dots/setas */
    --dot-size: 8px;
    --arrow-size: 28px;
    --ctrl-bottom: 18px;
    margin-bottom: 20px;  /* ajuda a não cortar conteúdo abaixo */
  }

  /* baseline estável de altura no conteúdo do slide */
  :deep(.mobile-carousel .v-window__container),
  :deep(.mobile-carousel .v-window-item) {
    min-height: 240px;
  }

  /* Centraliza e baixa os delimitadores (bolinhas) */
  :deep(.mobile-carousel .v-carousel__controls) {
    position: absolute;
    left: 0; right: 0;
    bottom: -1px;              /* como você usou */
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1px;
    background: transparent;
    box-shadow: none;
    padding: 0;
    width: 100%;
    margin: 0 auto;
    z-index: 2;
  }

  :deep(.mobile-carousel .v-carousel__controls .v-btn) {
    width: var(--dot-size) !important;
    height: var(--dot-size) !important;
    min-width: var(--dot-size) !important;
    min-height: var(--dot-size) !important;
    border-radius: 50% !important;
    margin: 0 4px !important;
    background-color: #dfe2e6 !important;
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    font-size: 0 !important;
  }
  :deep(.mobile-carousel .v-carousel__controls .v-btn.v-btn--active) {
    background-color: #7a8699 !important;
  }
  :deep(.mobile-carousel .v-carousel__controls .v-btn .v-btn__content),
  :deep(.mobile-carousel .v-carousel__controls .v-btn::before),
  :deep(.mobile-carousel .v-carousel__controls .v-btn .v-btn__overlay),
  :deep(.mobile-carousel .v-carousel__controls .v-btn .v-btn__underlay) {
    display: none !important;
  }

  /* Setas nas laterais, alinhadas à linha dos dots (sua versão) */
  .mobile-carousel .carousel-arrow {
    position: absolute;
    bottom: 60px; /* combinando com bottom:-20 dos dots + altura interna */
    width: var(--arrow-size);
    height: var(--arrow-size);
    min-width: var(--arrow-size);
    min-height: var(--arrow-size);
    background: transparent !important;
    box-shadow: none !important;
    z-index: 3;
  }
  .mobile-carousel .carousel-arrow.prev { left: 40px; }
  .mobile-carousel .carousel-arrow.next { right: 40px; }
  .mobile-carousel .carousel-arrow .v-icon {
    color: #7a8699 !important;
    font-size: 24px;
  }
}

/* ===== Proteção contra Owl Carousel ===== */
.v-carousel {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  position: relative !important;
  width: 100% !important;
  height: auto !important;
}
.v-carousel__container {
  display: block !important;
  visibility: visible !important;
  position: relative !important;
  width: 100% !important;
  height: 100% !important;
}
.v-carousel__item {
  display: flex !important;
  visibility: visible !important;
  position: relative !important;
  width: 100% !important;
  height: 100% !important;
}
/* Remove qualquer interferência do owl dentro do escopo do componente */
*[class*="owl"] {
  all: revert !important;
}

/* ===== Auxiliares ===== */
.v-slide-group__content { justify-content: center; }

h2 {
  font-size: 2em;
  margin-top: 1em;
  color: #555;
  i { font-size: 1.5em; margin-top: -5px; }
}
p.subtitle { font-size: 1em; color: gray; margin: 0.5em 0; }
.cursor-pointer { cursor: pointer; }

@media (min-width: 1024px) {
  .v-col-xl-3 {
    flex: 0 0 25% !important;
    max-width: 25% !important;
    padding: 20px;
  }
}
</style>
